import React, { useMemo, useState } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { Patient } from '../../types';
import { Users, UserPlus, Search, Calendar, Clock, CheckCircle, AlertCircle, Stethoscope, Baby, FileText, ArrowRight } from 'lucide-react';
import { PatientReports } from './PatientReports';
import { PatientReviewDueList } from './PatientReviewDueList';
import { PostnatalDueNotifications } from '../maternity/PostnatalDueNotifications';
interface Props {}
export const ReceptionDashboard: React.FC<Props> = () => {
  const {
    patients,
    addPatient,
    encounters,
    createEncounter,
    currentUser,
    getPatientEncounters,
    addMaternityFile,
    maternityFiles
  } = useData();
  const [activeTab, setActiveTab] = useState<'register' | 'queue' | 'reports' | 'reviewDue' | 'maternity'>('register');
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    sex: 'Male' as 'Male' | 'Female',
    age: '',
    maritalStatus: 'Single' as 'Married' | 'Single',
    address: '',
    contact: '',
    occupation: '',
    nextOfKin: '',
    nokContact: '',
    nokRelationship: '',
    allergies: '',
    hivStatus: 'Unknown' as 'Positive' | 'Negative' | 'Unknown',
    isVitalsCompleted: false
  });
  // Maternity Form State
  const [maternityForm, setMaternityForm] = useState({
    firstName: '',
    surname: '',
    otherNames: '',
    age: '',
    religion: '',
    churchAttended: '',
    residentialAddress: '',
    locationLandmark: '',
    phone: '',
    nokName: '',
    nokPhone: '',
    nokRelationship: '',
    spouseFirstName: '',
    spouseSurname: '',
    spouseOtherNames: '',
    spouseOccupation: '',
    spouseContact: ''
  });
  // Computed properties
  const activeEncounters = useMemo(() => {
    const now = Date.now();
    const upper = now + 24 * 60 * 60 * 1000;
    return encounters.filter(e => {
      if (!e.reviewDate) return false;
      if (e.reviewStatus === 'Cancelled') return false;
      const t = new Date(e.reviewDate).getTime();
      if (Number.isNaN(t)) return false;
      return t <= upper; // includes overdue (t < now) AND future-within-24h
    }).length;
  }, [encounters]);
  const filteredPatients = patients.filter(p => p.firstName.toLowerCase().includes(searchQuery.toLowerCase()) || p.lastName.toLowerCase().includes(searchQuery.toLowerCase()) || p.bmcFileNumber.toLowerCase().includes(searchQuery.toLowerCase()));
  const handleRegister = (e: React.FormEvent) => {
    e.preventDefault();
    const newPatient = addPatient({
      ...formData,
      age: parseInt(formData.age) || 0
    });
    // Auto create encounter and send to nursing
    createEncounter(newPatient.id);
    // Reset form and show success
    setFormData({
      firstName: '',
      lastName: '',
      sex: 'Male',
      age: '',
      maritalStatus: 'Single',
      address: '',
      contact: '',
      occupation: '',
      nextOfKin: '',
      nokContact: '',
      nokRelationship: '',
      allergies: '',
      hivStatus: 'Unknown'
    });
    alert(`Patient registered successfully! BMC Number: ${newPatient.bmcFileNumber}. Sent to Nursing.`);
    setActiveTab('search');
    setSearchQuery(newPatient.bmcFileNumber);
  };
  const handleRegisterMaternity = (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;
    const now = new Date();
    const yy = String(now.getFullYear()).slice(-2);
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const prefix = `MAT-${yy}${mm}`;
    const monthCount = maternityFiles.filter(m => m.bmcFileNumber.startsWith(prefix)).length;
    const sequence = String(monthCount + 1).padStart(3, '0');
    const bmcFileNumber = `${prefix}${sequence}`;
    addMaternityFile({
      bmcFileNumber,
      createdAt: new Date().toISOString(),
      createdBy: currentUser.id,
      stage: 'NURSING',
      patient: {
        firstName: maternityForm.firstName,
        surname: maternityForm.surname,
        otherNames: maternityForm.otherNames,
        age: maternityForm.age,
        religion: maternityForm.religion,
        churchAttended: maternityForm.churchAttended,
        residentialAddress: maternityForm.residentialAddress,
        locationLandmark: maternityForm.locationLandmark,
        phone: maternityForm.phone,
        nextOfKin: {
          name: maternityForm.nokName,
          phone: maternityForm.nokPhone,
          relationship: maternityForm.nokRelationship
        }
      },
      spouse: {
        firstName: maternityForm.spouseFirstName,
        surname: maternityForm.spouseSurname,
        otherNames: maternityForm.spouseOtherNames,
        occupation: maternityForm.spouseOccupation,
        contact: maternityForm.spouseContact
      },
      admission: {
        date: '',
        time: ''
      },
      generalExam: {
        temp: '',
        pulse: '',
        respiration: '',
        bp: '',
        oedema: '',
        anemia: '',
        cardiovascularSystem: '',
        respiratorySystem: '',
        centralNervousSystem: '',
        breasts: '',
        varicosities: '',
        vaginalDischarge: '',
        otherAbnormalities: '',
        msUrine: {
          sg: '',
          albumen: '',
          acetone: '',
          sugar: ''
        },
        hb: '',
        abo: '',
        rh: ''
      },
      presentPregnancy: {
        lmp: '',
        quickening: '',
        edd: '',
        gestationAge: '',
        ancSummaryFirstBp: '',
        ancSummaryAtWeeks: '',
        syphilisTest: {
          date: '',
          result: '',
          treatment: ''
        },
        hpTest: {
          date: '',
          result: ''
        },
        pelvicAssessment: '',
        abnormalities: ''
      },
      labourObservations: [],
      delivery: {
        laceration: '',
        anterior: '',
        posterior: '',
        none: '',
        repaired: '',
        membranes: '',
        membranesDate: '',
        membranesTime: '',
        episiotomy: '',
        episiotomyRepaired: '',
        maternalARV: '',
        arvTypes: '',
        uterineMassage: '',
        motherOnHAART: '',
        cytotec: '',
        syntometrine: '',
        firstStageBeganDate: '',
        firstStageBeganTime: '',
        placentaDelivery: '',
        completeDilationDate: '',
        completeDilationTime: '',
        placentaDeliveredDate: '',
        placentaDeliveredTime: '',
        abnormalityAtDilation: '',
        abnormalityAtDilationDesc: '',
        postpartumHemorrhage: '',
        deliveryMethod: '',
        ebl: '',
        abnormalityAtDelivery: '',
        abnormalityAtDeliveryDesc: '',
        complications: '',
        deliverySummary: []
      },
      newbornEvaluation: {
        ega: '',
        alive: '',
        neonatalDeath: '',
        causeOfDeath: '',
        apgar1: '',
        apgar5: '',
        apgar10: '',
        generalExam: '',
        congenitalMalformation: '',
        congenitalMalformationDesc: '',
        infantARVs: '',
        infantFeeding: '',
        resuscitationMethods: '',
        crownHeelLength: '',
        headCircumference: '',
        skinJaundice: '',
        skinOther: '',
        fontanelles: '',
        eyes: '',
        eyesTreatment: '',
        mouth: '',
        neck: '',
        chest: '',
        abdomen: '',
        umbilicus: '',
        genitalia: '',
        urine: '',
        anus: '',
        meconium: '',
        upperLimbs: '',
        erbParalysis: '',
        lowerLimbs: '',
        ortolaniSign: '',
        back: '',
        reflexesGrasp: '',
        reflexesMoro: '',
        reflexesSucking: '',
        progressNotes: ''
      },
      postDeliveryObservations: [],
      puerperiumNotes: []
    });
    setMaternityForm({
      firstName: '',
      surname: '',
      otherNames: '',
      age: '',
      religion: '',
      churchAttended: '',
      residentialAddress: '',
      locationLandmark: '',
      phone: '',
      nokName: '',
      nokPhone: '',
      nokRelationship: '',
      spouseFirstName: '',
      spouseSurname: '',
      spouseOtherNames: '',
      spouseOccupation: '',
      spouseContact: ''
    });
    alert(`Maternity patient registered successfully. File Number: ${bmcFileNumber}. File forwarded to Nursing.`);
  };
  const handleAddToQueue = (patientId: string) => {
    createEncounter(patientId);
    alert('Patient added to queue successfully.');
  };
  return <div className="space-y-6" data-id="element-6065" data-mp="3b0bkgahsn042">
      <div className="flex justify-between items-center" data-id="element-6066" data-mp="3b1mma4rohxsj">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-6067" data-mp="zkjdy1yontqx">
          Reception Dashboard
        </h1>
        <nav className="flex space-x-2" data-id="element-6068" data-mp="umhxzpwk0ajo">
          <button onClick={() => setActiveTab('register')} className={`px-4 py-2 font-medium text-sm rounded-t-lg ${activeTab === 'register' ? 'bg-white text-blue-600 border-t-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-6069" data-mp="ulgin98i91w5">
            <UserPlus className="w-4 h-4 inline-block mr-2 mb-1" data-id="element-6070" data-mp="gn9van16mkaq" />
            Register New
          </button>
          <button onClick={() => setActiveTab('queue')} className={`px-4 py-2 font-medium text-sm rounded-t-lg ${activeTab === 'queue' ? 'bg-white text-blue-600 border-t-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-6071" data-mp="bofcvm74mxr5">
            <Users className="w-4 h-4 inline-block mr-2 mb-1" data-id="element-6072" data-mp="211iupxtekwrb" />
            Queue
          </button>
          <button onClick={() => setActiveTab('reports')} className={`px-4 py-2 font-medium text-sm rounded-t-lg ${activeTab === 'reports' ? 'bg-white text-blue-600 border-t-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-6073" data-mp="1x1005w04lclf">
            <FileText className="w-4 h-4 inline-block mr-2 mb-1" data-id="element-6074" data-mp="nrgcvj82ply4" />
            Patient Reports
          </button>
          <button onClick={() => setActiveTab('reviewDue')} className={`px-4 py-2 font-medium text-sm rounded-t-lg ${activeTab === 'reviewDue' ? 'bg-white text-blue-600 border-t-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-6075" data-mp="dzaoskkxzoo7">
            Review Due
          </button>
          <button onClick={() => setActiveTab('maternity')} className={`px-4 py-2 font-medium text-sm rounded-t-lg ${activeTab === 'maternity' ? 'bg-white text-pink-600 border-t-2 border-pink-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-6076" data-mp="3w4dv28mna6q7">
            <Baby className="w-4 h-4 inline-block mr-2 mb-1" data-id="element-6077" data-mp="1peg18gavl1l0" />
            Maternity Registration
          </button>
        </nav>
      </div>

      <PostnatalDueNotifications files={maternityFiles} data-id="element-6078" data-mp="1wwxgl35fnalw" />

      {activeTab === 'register' && <Card title="Register New Patient" data-id="element-6079" data-mp="21w09jp851qdi">
          <form onSubmit={handleRegister} className="space-y-6" data-id="element-6080" data-mp="21uz01p6rtbxw">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-6081" data-mp="26p34o1skd7vt">
              <Input label="First Name" required value={formData.firstName} onChange={e => setFormData({
            ...formData,
            firstName: e.target.value
          })} data-id="element-6082" data-mp="2bnd0mem44cih" />
              <Input label="Last Name" required value={formData.lastName} onChange={e => setFormData({
            ...formData,
            lastName: e.target.value
          })} data-id="element-6083" data-mp="1nwhdhuslq5hk" />
              <Input label="Age" type="number" required value={formData.age} onChange={e => setFormData({
            ...formData,
            age: e.target.value
          })} data-id="element-6084" data-mp="33c7tk6xiypm1" />

              <Select label="Sex" options={[{
            value: 'Male',
            label: 'Male'
          }, {
            value: 'Female',
            label: 'Female'
          }]} required value={formData.sex} onChange={e => setFormData({
            ...formData,
            sex: e.target.value as any
          })} data-id="element-6085" data-mp="18b4bwg0c88c3" />
              <Select label="Marital Status" options={[{
            value: 'Single',
            label: 'Single'
          }, {
            value: 'Married',
            label: 'Married'
          }]} required value={formData.maritalStatus} onChange={e => setFormData({
            ...formData,
            maritalStatus: e.target.value as any
          })} data-id="element-6086" data-mp="1sfvs1rrcg3g" />
              <Input label="Contact Number" required value={formData.contact} onChange={e => setFormData({
            ...formData,
            contact: e.target.value
          })} data-id="element-6087" data-mp="2w8dmdnjfe2pz" />

              <Input label="Home Address" className="md:col-span-2" required value={formData.address} onChange={e => setFormData({
            ...formData,
            address: e.target.value
          })} data-id="element-6088" data-mp="16sntv3eyp83d" />
              <Input label="Occupation" value={formData.occupation} onChange={e => setFormData({
            ...formData,
            occupation: e.target.value
          })} data-id="element-6089" data-mp="1i9vjabv243a2" />
            </div>

            <div className="border-t border-gray-200 pt-4" data-id="element-6090" data-mp="25zg3ey2y56kx">
              <h4 className="text-md font-medium text-gray-900 mb-4" data-id="element-6091" data-mp="25xv999kjnpnl">
                Emergency Contact
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-6092" data-mp="1w1s5jza1niyj">
                <Input label="Next of Kin Name" required value={formData.nextOfKin} onChange={e => setFormData({
              ...formData,
              nextOfKin: e.target.value
            })} data-id="element-6093" data-mp="1w3ml0y2jqiv8" />
                <Input label="Relationship" required value={formData.nokRelationship} onChange={e => setFormData({
              ...formData,
              nokRelationship: e.target.value
            })} data-id="element-6094" data-mp="fikm2ug242x0" />
                <Input label="Contact Number" required value={formData.nokContact} onChange={e => setFormData({
              ...formData,
              nokContact: e.target.value
            })} data-id="element-6095" data-mp="y80oxof7s7gl" />
              </div>
            </div>

            <div className="flex justify-end pt-4" data-id="element-6096" data-mp="350tcjyvw09rv">
              <Button type="submit" size="lg" data-id="element-6097" data-mp="39yl3apksppuh">
                Register & Send to Nursing
              </Button>
            </div>
          </form>
        </Card>}

      {activeTab === 'queue' && <div className="space-y-6" data-id="element-6098" data-mp="2wz2iho6ah1fu">
          <Card data-id="element-6099" data-mp="2wzvebu3pm6j7">
            <div className="relative" data-id="element-6100" data-mp="3bv1l4jamq9dl">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-6101" data-mp="3bw32t6m5hu4a">
                <Search className="h-5 w-5 text-gray-400" data-id="element-6102" data-mp="36v5k8sx8ay29" />
              </div>
              <input type="text" className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Search by name or BMC number..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-6103" data-mp="3kuy72d5mwu4y" />
            </div>
          </Card>

          {searchQuery && !selectedPatient && <Card noPadding data-id="element-6104" data-mp="3d5ab8844im0">
              <ul className="divide-y divide-gray-200" data-id="element-6105" data-mp="m3s7dgc1jyjc">
                {filteredPatients.length > 0 ? filteredPatients.map(patient => <li key={patient.id} className="p-4 hover:bg-gray-50 cursor-pointer flex justify-between items-center" onClick={() => setSelectedPatient(patient)} data-id="element-6106" data-mp="r0iugnm9jue9">
                      <div data-id="element-6107" data-mp="3bzo4dmrxrot">
                        <p className="text-sm font-medium text-blue-600" data-id="element-6108" data-mp="3bpvsje9b8fy">
                          {patient.bmcFileNumber}
                        </p>
                        <p className="text-lg font-bold text-gray-900" data-id="element-6109" data-mp="3qia3c42cvez2">
                          {patient.firstName} {patient.lastName}
                        </p>
                        <p className="text-sm text-gray-500" data-id="element-6110" data-mp="3ugzerr3u2mg2">
                          {patient.age} yrs • {patient.sex} • {patient.contact}
                        </p>
                      </div>
                      <ArrowRight className="h-5 w-5 text-gray-400" data-id="element-6111" data-mp="3lkqiglb5k3y5" />
                    </li>) : <li className="p-8 text-center text-gray-500" data-id="element-6112" data-mp="3w12fot2m1a2s">
                    No patients found matching "{searchQuery}"
                  </li>}
              </ul>
            </Card>}

          {selectedPatient && <div className="space-y-6" data-id="element-6113" data-mp="3n0me5p5fgd0k">
              <Button variant="ghost" onClick={() => setSelectedPatient(null)} className="mb-2" data-id="element-6114" data-mp="3i1j944pg66sl">
                ← Back to search results
              </Button>

              <Card title="Patient Details" action={<Button onClick={() => handleAddToQueue(selectedPatient.id)} data-id="element-6116" data-mp="34b7dhvtnlpwp">
                    Add to Queue
                  </Button>} data-id="element-6115" data-mp="38d1bfwcgsft6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-id="element-6117" data-mp="2uc8cmzf4qyk9">
                  <div data-id="element-6118" data-mp="2uebceb634626">
                    <p className="text-sm text-gray-500" data-id="element-6119" data-mp="19ug0myhu5759">BMC Number</p>
                    <p className="font-medium" data-id="element-6120" data-mp="19wabfjze7ivm">
                      {selectedPatient.bmcFileNumber}
                    </p>
                  </div>
                  <div data-id="element-6121" data-mp="1js3vdoffyenf">
                    <p className="text-sm text-gray-500" data-id="element-6122" data-mp="1jtonz8i73nin">Full Name</p>
                    <p className="font-medium" data-id="element-6123" data-mp="1nugxm2uzbufg">
                      {selectedPatient.firstName} {selectedPatient.lastName}
                    </p>
                  </div>
                  <div data-id="element-6124" data-mp="w2srvz6pi65l">
                    <p className="text-sm text-gray-500" data-id="element-6125" data-mp="w29hd3jdyk1l">Age / Sex</p>
                    <p className="font-medium" data-id="element-6126" data-mp="w0f1i3mz1i4d">
                      {selectedPatient.age} / {selectedPatient.sex}
                    </p>
                  </div>
                  <div data-id="element-6127" data-mp="14wnxt9fnk0ma">
                    <p className="text-sm text-gray-500" data-id="element-6128" data-mp="14x7d0idx4awm">Contact</p>
                    <p className="font-medium" data-id="element-6129" data-mp="8cf5opasz1ix">{selectedPatient.contact}</p>
                  </div>
                  <div data-id="element-6130" data-mp="cdh79cs6zpo2">
                    <p className="text-sm text-gray-500" data-id="element-6131" data-mp="ccpc232ibtyb">Address</p>
                    <p className="font-medium" data-id="element-6132" data-mp="3fxygtu8uozog">{selectedPatient.address}</p>
                  </div>
                  <div data-id="element-6133" data-mp="3kwi541ax2njz">
                    <p className="text-sm text-gray-500" data-id="element-6134" data-mp="3kw03sq9edj57">Next of Kin</p>
                    <p className="font-medium" data-id="element-6135" data-mp="3p0pxq9khwnnh">
                      {selectedPatient.nextOfKin} ({selectedPatient.nokContact})
                    </p>
                  </div>
                  <div data-id="element-6136" data-mp="2x8s54r6h58c5">
                    <p className="text-sm text-gray-500" data-id="element-6137" data-mp="2x9jq782g3no6">Allergies</p>
                    <p className="font-medium text-red-600" data-id="element-6138" data-mp="319c502ifmq1z">
                      {selectedPatient.allergies}
                    </p>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-6" data-id="element-6139" data-mp="3azn8jny7ex8c">
                  <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center" data-id="element-6140" data-mp="3b0yb5kfwxyxv">
                    <Clock className="w-5 h-5 mr-2 text-gray-500" data-id="element-6141" data-mp="2djq49amew2rv" /> Visit
                    History
                  </h4>

                  {getPatientEncounters(selectedPatient.id).length > 0 ? <div className="space-y-3" data-id="element-6142" data-mp="2az1xtokjl1uy">
                      {getPatientEncounters(selectedPatient.id).map(enc => <div key={enc.id} className="bg-gray-50 p-4 rounded-lg border border-gray-200 flex justify-between items-center" data-id="element-6143" data-mp="211ngznupib24">
                          <div data-id="element-6144" data-mp="1s0pe58w089l4">
                            <p className="font-medium text-gray-900" data-id="element-6145" data-mp="1s1r048ek6ghm">
                              {new Date(enc.startedAt).toLocaleDateString()} at{' '}
                              {new Date(enc.startedAt).toLocaleTimeString()}
                            </p>
                            <p className="text-sm text-gray-500" data-id="element-6146" data-mp="1dccp6vvc3r04">
                              {enc.lastAttendingClinician ? `Seen by ${enc.lastAttendingClinician}` : 'No clinician assigned'}
                            </p>
                          </div>
                          <Badge variant={enc.stage === 'COMPLETED' ? 'success' : 'warning'} data-id="element-6147" data-mp="uniwtxigkl85">
                            {enc.stage}
                          </Badge>
                        </div>)}
                    </div> : <p className="text-gray-500 italic" data-id="element-6148" data-mp="1ddqnhg4z1j7g">
                      No previous visits found.
                    </p>}
                </div>
              </Card>
            </div>}
        </div>}

      {activeTab === 'reports' && <PatientReports data-id="element-6149" data-mp="13mmjfbgu3ire" />}

      {activeTab === 'reviewDue' && <PatientReviewDueList data-id="element-6150" data-mp="18mif8nfdo3vv" />}

      {activeTab === 'maternity' && <Card title="Register Maternity Patient" data-id="element-6151" data-mp="1wcmfqlx9rnin">
          <form onSubmit={handleRegisterMaternity} className="space-y-6" data-id="element-6152" data-mp="1wbtg04wq9ya0">
            <div data-id="element-6153" data-mp="214w9ye5f39qq">
              <h3 className="text-lg font-medium text-gray-900 mb-4 border-b pb-2" data-id="element-6154" data-mp="215fmtgg7n7xw">
                Patient Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-6155" data-mp="3gv6o3zvzjgoz">
                <Input label="First Name" required value={maternityForm.firstName} onChange={e => setMaternityForm({
              ...maternityForm,
              firstName: e.target.value
            })} data-id="element-6156" data-mp="3guwzogql9hle" />
                <Input label="Surname" required value={maternityForm.surname} onChange={e => setMaternityForm({
              ...maternityForm,
              surname: e.target.value
            })} data-id="element-6157" data-mp="2xx2p3y9tay0w" />
                <Input label="Other Names" value={maternityForm.otherNames} onChange={e => setMaternityForm({
              ...maternityForm,
              otherNames: e.target.value
            })} data-id="element-6158" data-mp="r7o8j9cqzt3e" />
                <Input label="Age" type="number" required value={maternityForm.age} onChange={e => setMaternityForm({
              ...maternityForm,
              age: e.target.value
            })} data-id="element-6159" data-mp="1reuxhrj54vxz" />
                <Input label="Phone Number" required value={maternityForm.phone} onChange={e => setMaternityForm({
              ...maternityForm,
              phone: e.target.value
            })} data-id="element-6160" data-mp="2a5m1edkczu4z" />
                <Input label="Religion" value={maternityForm.religion} onChange={e => setMaternityForm({
              ...maternityForm,
              religion: e.target.value
            })} data-id="element-6161" data-mp="2o5wu7m58biss" />
                <Input label="Church Attended" value={maternityForm.churchAttended} onChange={e => setMaternityForm({
              ...maternityForm,
              churchAttended: e.target.value
            })} data-id="element-6162" data-mp="3j6koihnz0m6" />
                <div className="md:col-span-2" data-id="element-6163" data-mp="mf66fbk1t7xb">
                  <Input label="Residential Address" required value={maternityForm.residentialAddress} onChange={e => setMaternityForm({
                ...maternityForm,
                residentialAddress: e.target.value
              })} data-id="element-6164" data-mp="mdv31cuim26q" />
                </div>
                <div className="md:col-span-3" data-id="element-6165" data-mp="38fr5unmoemkp">
                  <Input label="Location / Landmark" value={maternityForm.locationLandmark} onChange={e => setMaternityForm({
                ...maternityForm,
                locationLandmark: e.target.value
              })} data-id="element-6166" data-mp="38eg0wkhhv8s0" />
                </div>
              </div>
            </div>

            <div data-id="element-6167" data-mp="2bp8fdcgvkzo3">
              <h3 className="text-lg font-medium text-gray-900 mb-4 border-b pb-2" data-id="element-6168" data-mp="2bpqm5315ywa9">
                Next of Kin
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-6169" data-mp="2golcqr5dhp4x">
                <Input label="Name" required value={maternityForm.nokName} onChange={e => setMaternityForm({
              ...maternityForm,
              nokName: e.target.value
            })} data-id="element-6170" data-mp="9xc6ybq085xj" />
                <Input label="Phone" required value={maternityForm.nokPhone} onChange={e => setMaternityForm({
              ...maternityForm,
              nokPhone: e.target.value
            })} data-id="element-6171" data-mp="jzfwa3jnu4co" />
                <Input label="Relationship" required value={maternityForm.nokRelationship} onChange={e => setMaternityForm({
              ...maternityForm,
              nokRelationship: e.target.value
            })} data-id="element-6172" data-mp="12qyw61ie12ah" />
              </div>
            </div>

            <div data-id="element-6173" data-mp="3eiw2bt7vkeor">
              <h3 className="text-lg font-medium text-gray-900 mb-4 border-b pb-2" data-id="element-6174" data-mp="3ejdwmkaz8iu1">
                Spouse Details (Optional)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-6175" data-mp="bvoi8b3ot9nz">
                <Input label="First Name" value={maternityForm.spouseFirstName} onChange={e => setMaternityForm({
              ...maternityForm,
              spouseFirstName: e.target.value
            })} data-id="element-6176" data-mp="btvbywwzkrgi" />
                <Input label="Surname" value={maternityForm.spouseSurname} onChange={e => setMaternityForm({
              ...maternityForm,
              spouseSurname: e.target.value
            })} data-id="element-6177" data-mp="3p3rl5vzxd32k" />
                <Input label="Other Names" value={maternityForm.spouseOtherNames} onChange={e => setMaternityForm({
              ...maternityForm,
              spouseOtherNames: e.target.value
            })} data-id="element-6178" data-mp="18g6zkglrcbvz" />
                <Input label="Occupation" value={maternityForm.spouseOccupation} onChange={e => setMaternityForm({
              ...maternityForm,
              spouseOccupation: e.target.value
            })} data-id="element-6179" data-mp="1dqj64k2i18vj" />
                <Input label="Contact" value={maternityForm.spouseContact} onChange={e => setMaternityForm({
              ...maternityForm,
              spouseContact: e.target.value
            })} data-id="element-6180" data-mp="ztmh8o5xy1ha" />
              </div>
            </div>

            <div className="flex justify-end pt-4" data-id="element-6181" data-mp="2l67enqsnlwpa">
              <Button type="submit" size="lg" className="bg-pink-600 hover:bg-pink-700 text-white" data-id="element-6182" data-mp="3ah26td97qrk1">
                Register & Forward to Nursing
              </Button>
            </div>
          </form>
        </Card>}
    </div>;
};